import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'
import type { EnpsConfigListItem, SearchEnpsConfigsInput } from '../types'

interface Organization {
	id: string
	name: string
	logo?: string | null
}

/**
 * @fileoverview eNPS Dashboard Store
 *
 * Zustand store for eNPS list page with search, pagination, and actions
 */

export interface EnpsDashboardStore {
	// State
	enpsConfigs: EnpsConfigListItem[]
	organization: Organization | null
	isLoading: boolean
	error: string | null
	searchQuery: string
	selectedConfig: EnpsConfigListItem | null
	pagination: {
		page: number
		perPage: number
		totalCount: number
		totalPages: number
	}

	// Actions
	initialize: (organizationId: string) => Promise<void>
	search: (params: Partial<SearchEnpsConfigsInput>) => Promise<void>
	setSearchQuery: (query: string) => void
	setSelectedConfig: (config: EnpsConfigListItem | null) => void
	deleteSelectedConfig: () => Promise<void>
	duplicateSelectedConfig: () => Promise<void>
	setPage: (page: number) => void
}

export const createEnpsDashboardStore = () => {
	return create<EnpsDashboardStore>()(
		devtools(
			(set, get) => ({
				// Initial state
				enpsConfigs: [],
				organization: null,
				isLoading: false,
				error: null,
				searchQuery: '',
				selectedConfig: null,
				pagination: {
					page: 1,
					perPage: 20,
					totalCount: 0,
					totalPages: 0,
				},

				// Initialize - load initial data
				initialize: async (organizationId: string) => {
					// Guard against re-initialization for the same org
					if (get().organization?.id === organizationId || get().isLoading) {
						return
					}

					set({ isLoading: true, error: null })

					try {
						// Fetch organization and eNPS configs in parallel
						const [orgResult, enpsResult] = await Promise.all([
							trpcClient.admin.organizations.getById.query({ id: organizationId }),
							trpcClient.admin.enps.search.query({
								organizationId,
								page: 1,
								perPage: 20,
							}),
						])

						set({
							organization: { id: orgResult.id, name: orgResult.name, logo: orgResult.logo },
							enpsConfigs: enpsResult.data,
							pagination: enpsResult.pagination,
							isLoading: false,
						})
					} catch (error) {
						console.error('Failed to initialize NPS dashboard:', error)
						set({
							error: 'Failed to load NPS configurations',
							isLoading: false,
						})
					}
				},

				// Search with filters
				search: async (params: Partial<SearchEnpsConfigsInput>) => {
					const { organization } = get()
					const organizationId = params.organizationId || organization?.id

					// Guard against concurrent searches or missing organization
					if (get().isLoading || !organizationId) {
						return
					}

					set({ isLoading: true, error: null })

					try {
						const result = await trpcClient.admin.enps.search.query({
							organizationId,
							search: params.search || get().searchQuery,
							page: params.page || get().pagination.page,
							perPage: params.perPage || get().pagination.perPage,
							status: params.status,
							cadence: params.cadence,
							sortBy: params.sortBy,
							order: params.order,
						})

						set({
							enpsConfigs: result.data,
							pagination: result.pagination,
							isLoading: false,
						})
					} catch (error) {
						console.error('Failed to search NPS configs:', error)
						set({
							error: 'Failed to search NPS configurations',
							isLoading: false,
						})
					}
				},

				// Set search query
				setSearchQuery: (query: string) => {
					set({ searchQuery: query })
				},

				// Set selected config
				setSelectedConfig: (config: EnpsConfigListItem | null) => {
					set({ selectedConfig: config })
				},

				// Delete selected config
				deleteSelectedConfig: async () => {
					const { selectedConfig, organization } = get()
					if (!selectedConfig || !organization) return

					set({ isLoading: true, error: null })

					try {
						await trpcClient.admin.enps.delete.mutate({
							id: selectedConfig.id,
							organizationId: organization.id,
						})

						// Refresh list
						await get().search({})

						set({ selectedConfig: null, isLoading: false })
					} catch (error) {
						console.error('Failed to delete NPS config:', error)
						set({
							error: 'Failed to delete NPS configuration',
							isLoading: false,
						})
						throw error
					}
				},

				// Duplicate selected config
				duplicateSelectedConfig: async () => {
					const { selectedConfig, organization } = get()
					if (!selectedConfig || !organization) return

					set({ isLoading: true, error: null })

					try {
						await trpcClient.admin.enps.create.mutate({
							organizationId: organization.id,
							name: `${selectedConfig.name} (Copy)`,
							description: selectedConfig.description || undefined,
							cadence: selectedConfig.cadence,
							populationId: selectedConfig.populationId || undefined,
							questionText: selectedConfig.questionText,
							followUpQuestionText: selectedConfig.followUpQuestionText,
						})

						// Refresh list
						await get().search({})

						set({ selectedConfig: null, isLoading: false })
					} catch (error) {
						console.error('Failed to duplicate NPS config:', error)
						set({
							error: 'Failed to duplicate NPS configuration',
							isLoading: false,
						})
						throw error
					}
				},

				// Set page
				setPage: (page: number) => {
					set((state) => ({
						pagination: { ...state.pagination, page },
					}))
				},
			}),
			{ name: 'enps-dashboard-store' },
		),
	)
}
